import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';

import Button from '../../components/ui/Button';
import VoicePlayer from './components/VoicePlayer';
import WhiteboardDisplay from './components/WhiteboardDisplay';
import TeachingContent from './components/TeachingContent';
import ProgressTracker from './components/ProgressTracker';

const BABOKTeaching = () => {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);

  const lessons = [
    {
      id: 1,
      title: "Introduction to BABOK Guide",
      knowledgeArea: "Overview",
      duration: "8:30",
      content: "Welcome to the BABOK Guide professional teaching series. The Business Analysis Body of Knowledge, or BABOK Guide, is the globally recognized standard for the practice of business analysis. It describes business analysis knowledge areas, their associated activities and tasks, and the skills necessary to be effective in their execution.",
      highlights: ["BABOK Overview", "Purpose and Structure", "Key Concepts"]
    },
    {
      id: 2,
      title: "Business Analysis Planning and Monitoring",
      knowledgeArea: "Planning",
      duration: "12:45",
      content: "Business Analysis Planning and Monitoring involves defining and coordinating the activities required to perform business analysis. This knowledge area covers how to plan the business analysis approach, stakeholder engagement, governance, and information management. It ensures that business analysis activities are properly planned, monitored, and adjusted as needed throughout the initiative.",
      highlights: ["Planning Approach", "Stakeholder Engagement", "Governance", "Information Management"]
    },
    {
      id: 3,
      title: "Elicitation and Collaboration",
      knowledgeArea: "Elicitation",
      duration: "15:20",
      content: "Elicitation and Collaboration describes how business analysts work with stakeholders to identify and understand their needs and concerns. This knowledge area covers preparing for elicitation, conducting elicitation activities, confirming results, communicating business analysis information, and managing stakeholder collaboration. Effective elicitation is critical to understanding stakeholder needs and ensuring successful outcomes.",
      highlights: ["Elicitation Preparation", "Conducting Elicitation", "Confirming Results", "Stakeholder Collaboration"]
    },
    {
      id: 4,
      title: "Requirements Life Cycle Management",
      knowledgeArea: "Requirements",
      duration: "14:10",
      content: "Requirements Life Cycle Management involves managing and maintaining requirements and design information from inception to retirement. This includes tracing requirements, maintaining requirements for reuse, prioritizing requirements, assessing changes, and approving requirements. Proper requirements management ensures that requirements remain aligned with business objectives throughout the solution lifecycle.",
      highlights: ["Requirements Tracing", "Maintenance", "Prioritization", "Change Assessment", "Approval"]
    },
    {
      id: 5,
      title: "Strategy Analysis",
      knowledgeArea: "Strategy",
      duration: "13:55",
      content: "Strategy Analysis describes the business analysis work that must be performed to collaborate with stakeholders to identify a need of strategic or tactical importance, enable the enterprise to address that need, and align the resulting strategy for the change with higher and lower level strategies. This knowledge area focuses on understanding the current state, defining the future state, assessing risks, and defining the change strategy.",
      highlights: ["Current State Analysis", "Future State Definition", "Risk Assessment", "Change Strategy"]
    },
    {
      id: 6,
      title: "Requirements Analysis and Design Definition",
      knowledgeArea: "Analysis",
      duration: "16:30",
      content: "Requirements Analysis and Design Definition describes how business analysts progressively elaborate and structure requirements and designs. This knowledge area covers specifying and modeling requirements, verifying requirements, validating requirements, defining requirements architecture, and defining design options. It ensures that requirements are analyzed, structured, and specified in sufficient detail to enable effective solution development.",
      highlights: ["Requirements Specification", "Modeling", "Verification", "Validation", "Architecture", "Design Options"]
    },
    {
      id: 7,
      title: "Solution Evaluation",
      knowledgeArea: "Evaluation",
      duration: "11:40",
      content: "Solution Evaluation describes how business analysts assess the performance of and value delivered by a solution in use by the enterprise, and recommend removal of barriers or constraints that prevent the full realization of the value. This includes measuring solution performance, analyzing performance measures, assessing solution limitations, and recommending actions to increase solution value. Effective solution evaluation ensures that solutions continue to deliver value over time.",
      highlights: ["Performance Measurement", "Performance Analysis", "Limitation Assessment", "Value Optimization"]
    }
  ];

  const handleLessonComplete = () => {
    if (!completedLessons?.includes(currentLesson)) {
      setCompletedLessons([...completedLessons, currentLesson]);
    }
  };

  const handleNextLesson = () => {
    if (currentLesson < lessons?.length - 1) {
      setCurrentLesson(currentLesson + 1);
      setIsPlaying(false);
    }
  };

  const handlePreviousLesson = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      setIsPlaying(false);
    }
  };

  const progressPercentage = Math.round((completedLessons?.length / lessons?.length) * 100);

  return (
    <>
      <Helmet>
        <title>BABOK Teaching - Professional Voice Instruction | BABOK Master Pro</title>
        <meta name="description" content="Learn the complete BABOK Guide with professional human voice instruction and interactive whiteboard display in a classroom setting." />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="container mx-auto px-4 py-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-h3 md:text-h2 font-heading text-foreground mb-2">
                  BABOK Professional Teaching
                </h1>
                <p className="text-body text-muted-foreground">
                  Interactive classroom experience with professional voice instruction
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="BookOpen"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  {showTranscript ? 'Hide' : 'Show'} Transcript
                </Button>
              </div>
            </div>
          </div>

          {/* Progress Tracker */}
          <ProgressTracker
            currentLesson={currentLesson}
            totalLessons={lessons?.length}
            completedLessons={completedLessons}
            progressPercentage={progressPercentage}
          />

          {/* Main Teaching Interface */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Left Side - Whiteboard Display */}
            <WhiteboardDisplay
              currentLesson={lessons?.[currentLesson]}
              isPlaying={isPlaying}
            />

            {/* Right Side - Teaching Content & Controls */}
            <div className="space-y-6">
              <TeachingContent
                lesson={lessons?.[currentLesson]}
                showTranscript={showTranscript}
              />

              <VoicePlayer
                lesson={lessons?.[currentLesson]}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                playbackSpeed={playbackSpeed}
                setPlaybackSpeed={setPlaybackSpeed}
                onLessonComplete={handleLessonComplete}
              />
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Button
                variant="outline"
                iconName="ChevronLeft"
                onClick={handlePreviousLesson}
                disabled={currentLesson === 0}
              >
                Previous Lesson
              </Button>

              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">
                  Lesson {currentLesson + 1} of {lessons?.length}
                </p>
                <p className="text-base font-semibold text-foreground">
                  {lessons?.[currentLesson]?.title}
                </p>
              </div>

              <Button
                variant="default"
                iconName="ChevronRight"
                iconPosition="right"
                onClick={handleNextLesson}
                disabled={currentLesson === lessons?.length - 1}
              >
                Next Lesson
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BABOKTeaching;